import LocalStorageUtils from "utils/LocalStorage"
import api from "services/api"
import axios from "axios"
import unauthenticatedApi from "services/unauthenticatedApi"
import { dismissAllToast, errorToast } from "utils/Toast"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import moment from "moment"
import Constant from "utils/Constants"
import { useRollbar } from "@rollbar/react"

export const BASE_API_CONSTANT = {
    METHOD: {
        POST: 'POST',
        PUT: 'PUT',
        GET: 'GET',
        DELETE: 'DELETE'
    }
}

const useBaseApiHook = (initialLoading = false, showError = true) => {
    const [isLoading, setIsLoading] = useState(initialLoading)
    const [error, setError] = useState(null)
    const [data, setData] = useState(null)
    const {t} = useTranslation()
    const rollbar = useRollbar();

    const executeApi = async (url, method, data, axiosOptions = {}) => {
        setIsLoading(true)

        const { isUnauthenticated = false } = axiosOptions
        const _timestamp = moment(moment().format('MM-DD-YYYY HH:mm:00'), "MM-DD-YYYY HH:mm:ss").format('x')
        const _url = `${url}?timestamp=${_timestamp}`
        
        switch (method) {
            case BASE_API_CONSTANT.METHOD.POST:
                try {
                    let response;

                    if (isUnauthenticated) response = await unauthenticatedApi.post(_url, data, axiosOptions)
                    else response = await api.post(_url, data, axiosOptions)

                    if (response?.status === 200) {
                        setIsLoading(false)
                        if (!response?.data?.isSuccess) {
                            if (showError) {
                                dismissAllToast()
                                let errMessage = response?.data?.message ? t(response?.data?.message) : `Failed getting data on ${url}`
                                if(!response?.data?.message) {
                                    rollbar.error(`Error getting data on ${url}`, {
                                        'response': response
                                    })
                                }
                                errorToast(errMessage)
                            }
                        }
                        setData(response?.data)

                        return response?.data
                    }

                    return false

                } catch (e) {
                    setIsLoading(false)
                    setData(null)
                    dismissAllToast()
                    setError(e?.response?.data)

                    if(e?.code === "ERR_NETWORK" || e?.message === "timeout exceeded") {
                        errorToast(t('globalAlert.canNotConnectToServer', {url}))
                        rollbar.error(`Can not connect to server in ${url}`, {
                            'user_token': LocalStorageUtils.jwtToken.get(),
                            'device': navigator?.userAgentData,
                            'reason': e?.message,
                            'response': e?.response
                        })

                        return {
                            isSuccess: false,
                            isError: true,
                            message: t('globalAlert.canNotConnectToServerUrl'),
                            error: e
                        }
                    }

                    if (showError) {
                        let errMessage = e?.response?.data?.error ? t(e?.response?.data?.error) : `Failed getting data on ${url}`
                        if(!e?.response?.data?.error) {
                            rollbar.error(`Error getting data on ${url}`, {
                                'user_token': LocalStorageUtils.jwtToken.get(),
                                'response': e?.response
                            })
                        }
                        errorToast(errMessage)
                    }
                    return false
                }

            case BASE_API_CONSTANT.METHOD.DELETE:
                try {
                    let response;

                    if (isUnauthenticated) response = await unauthenticatedApi.delete(url, data, axiosOptions)
                    else response = await api.delete(url, data, axiosOptions)

                    if (response?.status === 200) {
                        setIsLoading(false)
                        if (!response?.data?.isSuccess) {
                            // errorToast(response?.data || "Request Failed")
                        }
                        setData(response?.data)

                        return response?.data
                    }

                    return false

                } catch (e) {
                    setIsLoading(false)
                    setData(null)
                    dismissAllToast()

                    let errMessage = e?.response?.data?.error ? t(e?.response?.data?.error) : `Failed getting data on ${url}`
                    errorToast(errMessage)
                    setError(e?.response?.data)
                    return false
                }

            default:
                try {
                    let response;

                    if (isUnauthenticated) response = await unauthenticatedApi.get(_url, axiosOptions)
                    else response = await api.get(_url, axiosOptions)

                    if (response?.status === 200) {
                        setIsLoading(false)
                        setData(response?.data)
                        dismissAllToast()

                        if (!response?.data?.isSuccess) {
                            let errMessage = response?.data?.message ? t(response?.data?.message) : `Failed getting data on ${url}`
                            if(!response?.data?.message) {
                                rollbar.error(`Error getting data on ${url}`, {
                                    'response': response
                                })
                            }
                            if (showError) errorToast(errMessage)
                        }
                        return response?.data
                    }

                    return false

                } catch (e) {
                    setIsLoading(false)
                    setData(null)
                    dismissAllToast()

                    if(e?.code === "ERR_NETWORK" || e?.message === "timeout exceeded") {
                        errorToast(t('globalAlert.canNotConnectToServer'))
                        rollbar.error(`Can not connect to server in ${url}`, {
                            'user_token': LocalStorageUtils.jwtToken.get(),
                            'device': navigator?.userAgentData,
                            'response': e?.response,
                            'reason': e?.message
                        })

                        return {
                            isSuccess: false,
                            isError: true,
                            message: t('globalAlert.canNotConnectToServerUrl'),
                            error: e
                        }
                    }

                    if (showError) {

                        if(!e?.response?.data?.error) {
                            rollbar.error(`Error getting data on ${url}`, {
                                'response': e?.response
                            })
                        }
                        errorToast(e?.message)
                    }

                    setError(e?.response?.data)
                    if (e?.name === "AxiosError") {
                        return {
                            isSuccess: false,
                            isError: true,
                            message: e?.message,
                            error: e
                        }
                    }
                    return false
                }
        }
    }

    const uploadSingleFile = async (url, formData) => {
        setIsLoading(true)

        const token = LocalStorageUtils.jwtToken.get()

        const uploadApi = axios.create({
            baseURL: Constant.baseApiURL,
            timeout: Constant.apiTimeout,
            headers: {
                "Accept": "*/*",
                "Content-Type": 'multipart/form-data',
                "Access-Control-Allow-Origin": '*',
                "Authorization": `Bearer ${token}`
            },
            data
        })

        try {
            let response = await uploadApi.post(url, formData)
            if (response?.status === 200) {
                setIsLoading(false)
                setData(response?.data)
                if (!response?.data?.isSuccess) {
                    dismissAllToast()
                    errorToast(response?.data?.message || "Request Failed")
                }
                return response?.data
            }

            return false
        } catch (e) {
            setIsLoading(false)
            setData(null)
            dismissAllToast()
            errorToast(e?.response?.data?.error || "Request Failed")
            setError(e?.response?.data)
            return false
        }
    }

    const uploadMultipleFile = async (url, formData) => {
        setIsLoading(true)

        const token = LocalStorageUtils.jwtToken.get()

        const uploadApi = axios.create({
            baseURL: Constant.baseApiURL,
            method: "post",
            timeout: Constant.apiTimeout,
            headers: {
                "Accept": "*/*",
                'Content-Type': 'multipart/form-data',
                "Access-Control-Allow-Origin": "*",
                "Authorization": `Bearer ${token}`
            },
            data,
        })

        try {
            let response = await uploadApi.post(url, formData)
            if (response?.status === 200) {
                setIsLoading(false)
                setData(response?.data)
                if (!response?.data?.isSuccess) {
                    dismissAllToast()
                    errorToast(response?.data?.message || "Request Failed")
                }
                return response?.data
            }

            return false
        } catch (e) {
            setIsLoading(false)
            setData(null)
            dismissAllToast()
            errorToast(e?.response?.data?.error || "Request Failed")
            setError(e?.response?.data)
            return false
        }
    }

    const handleBlobResponse = (blobFileData) => {
        // debugger
        const url = window.URL.createObjectURL(new Blob([blobFileData?.data]))
        const link = document.createElement('a')
        const filename = blobFileData?.headers?.['content-disposition']?.split('filename=')?.[1];
        link.href = url
        link.setAttribute('download', filename) // nama file dan extension sesuaikan dengan file yang di download
        document.body.appendChild(link)
        link.click()
    }

    const download = async (url) => {
        setIsLoading(true)

        const downloadApi = axios.create({
            responseType: 'blob',
            headers: {
                "Access-Control-Allow-Origin": '*'
            }
        })

        try {
            let response = await downloadApi.get(url)
            if (response?.status === 200) {
                handleBlobResponse(response)
                setIsLoading(false)
                return true
            }

            return false
        } catch (e) {
            setIsLoading(false)
            errorToast(e?.response?.data?.error || "Request Failed")
            setError(e?.response?.data)
            return false
        }
    }

    return {
        isLoading,
        data,
        error,
        executeApi,
        uploadSingleFile,
        download,
        uploadMultipleFile
    }
}

export default useBaseApiHook
